import React, { useState, useEffect } from "react";
import { Review } from "../../types/ReviewTypes";
import { authFetch } from "../../utils/authUtils";
import StarRating from "../Common/StarRating";
import "./Review.css";
import "../Common/StarRating.css";

interface ReviewFormInlineProps {
    songId?: number;
    itemId?: number;
    itemType?: 'song' | 'album';
    existingReview?: Review | null;
    onReviewAdded: (review: Review) => void;
    onReviewUpdated: (review: Review) => void;
    onCancel: () => void;
}

interface ReviewFormData {
    rating: number;
    content: string;
}

const ReviewFormInline: React.FC<ReviewFormInlineProps> = ({
    songId,
    itemId,
    itemType = 'song',
    existingReview,
    onReviewAdded,
    onReviewUpdated,
    onCancel
}) => {
    // Support both legacy songId and new itemId approach
    const actualItemId = itemId || songId;
    const actualItemType = itemType;

    const [formData, setFormData] = useState<ReviewFormData>({
        rating: existingReview ? Number(existingReview.rating) : 0,
        content: existingReview?.content || ""
    });
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Check if review is ready to submit
    const isReadyToSubmit = formData.rating > 0 && (hasChanges || !existingReview);

    useEffect(() => {
        if (existingReview) {
            setFormData({
                rating: Number(existingReview.rating),
                content: existingReview.content
            });
            setHasChanges(false);
        } else {
            setFormData({
                rating: 0,
                content: " "
            });
            setHasChanges(false);
        }
    }, [existingReview]);

    const handleRatingChange = (newRating: number) => {
        setFormData(prev => ({ ...prev, rating: newRating }));
        setHasChanges(true);
    };

    const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setFormData(prev => ({ ...prev, content: e.target.value }));
        setHasChanges(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.rating === 0) {
            setError("Please provide a rating");
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            let response;

            if (existingReview) {
                // Update existing review
                response = await authFetch(`/api/reviews/${existingReview.id}`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(formData)
                });
            } else {
                // Create new review - handle both song and album reviews
                const apiEndpoint = actualItemType === 'album'
                    ? "/api/reviews/album"
                    : "/api/reviews";

                response = await authFetch(apiEndpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        [`${actualItemType}_id`]: actualItemId,
                        ...formData
                    })
                });
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to submit review");
            }

            const reviewData = await response.json();

            if (existingReview) {
                onReviewUpdated(reviewData);
            } else {
                onReviewAdded(reviewData);
            }

            // Dispatch custom event to notify that a review was added or updated
            window.dispatchEvent(new CustomEvent('reviewUpdated'));

        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="review-form-inline">
            <form onSubmit={handleSubmit} className="review-form">
                <div className="review-content-section">
                    <label htmlFor="review-content" className="form-label">
                        <p className="rating-label">{existingReview ? "Edit Review" : "Add Review"}</p>
                        <div className="rating-container">
                            <StarRating
                                rating={formData.rating}
                                interactive={true}
                                onRatingChange={handleRatingChange}
                                size="large"
                                showValue={false}
                                className="review-star-rating"
                            />
                        </div>
                    </label>
                    <textarea
                        id="review-content"
                        className="review-textarea"
                        value={formData.content}
                        onChange={handleContentChange}
                        placeholder="Write your review here..."
                        rows={4}
                    />
                </div>

                {error && <div className="error-message">{error}</div>}

                <div className="form-actions">
                    {/* Only show cancel button when editing an existing review */}
                    {existingReview && (
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={onCancel}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                    )}
                    <button
                        type="submit"
                        className={`submit-button ${isReadyToSubmit ? 'ready' : ''}`}
                        disabled={isSubmitting || !isReadyToSubmit}
                    >
                        {isSubmitting ? "Submitting..." : existingReview ? "Update" : "Submit"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ReviewFormInline; 